import React from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { Container, Row, Col, Card, Badge, BreadcrumbItem, Nav, Breadcrumb, Dropdown } from "react-bootstrap";
import { Link } from "react-router-dom";
import { BiLeaf } from "react-icons/bi";
import { LuShieldPlus } from "react-icons/lu";
import { AiOutlinePercentage } from "react-icons/ai";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title as ChartTitle,
  Tooltip,
  Legend,
  BarElement,
} from "chart.js";
// import { Line } from "react-chartjs-2"; // Removed unused import
// import axios from "axios"; // Removed unused import

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ChartTitle, Tooltip, Legend, BarElement);
// ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend); // Remove unused Bar chart registration

const API_BASE_URL = process.env.REACT_APP_URL;
const API_ENDPOINT = "/api/WebServiceQI.aspx";

const AdminDashboard = () => {
  const { userInfo } = useSelector((state) => state.auth);
  const userName = userInfo && userInfo[2]?.InfoField ? userInfo[2].InfoField : "User";

  // Real data state
  const [quickInfoData, setQuickInfoData] = React.useState([]);

  // Month selection state
  const getPreviousMonthDate = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth() - 1, 1);
  };
  const [selectedDate, setSelectedDate] = React.useState(getPreviousMonthDate());

  // Generate month/year options for dropdown (last 12 months)
  const getMonthOptions = () => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = date.toISOString();
      const label = date.toLocaleString("default", { month: "long", year: "numeric" });
      options.push({ value, label });
    }
    return options;
  };
  const monthOptions = getMonthOptions();

  // Helper function to find specific data items
  const findItem = (keyword) => {
    try {
      return (
        quickInfoData.find((item) => (item.ItemName || "").toLowerCase().includes(keyword.toLowerCase())) || {
          ItemName: "N/A",
          InfoField: "-",
        }
      );
    } catch (error) {
      return { ItemName: "N/A", InfoField: "-" };
    }
  };
  const formatCurrency = (amount) => {
    if (!amount || amount === "-") return "₹0";
    return `₹${parseFloat(amount).toLocaleString("en-IN")}`;
  };

  React.useEffect(() => {
    const fetchData = async () => {
      if (!userInfo) return;
      try {
        const month = selectedDate.getMonth() + 1;
        const year = selectedDate.getFullYear();
        const reqMonth = month.toString().padStart(2, "0");
        const reqYear = year.toString();
        const companyCode = userInfo[13]?.InfoField || "1";
        const formData = new FormData();
        formData.append("title", "MakeSalarySummaryForQI");
        formData.append("ReqCompanyCode", companyCode);
        formData.append("ReqYear", reqYear);
        formData.append("ReqMonth", reqMonth);
        const response = await axios.post(`${API_BASE_URL}${API_ENDPOINT}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 10000,
        });
        if (response.status === 200) {
          const jsonResponse = response.data.split("||JasonEnd")[0].trim();
          const decodedJson = JSON.parse(jsonResponse);
          const decodedJsonData = JSON.parse(decodedJson[0]["JSONData1"]);
          setQuickInfoData(decodedJsonData);
        } else {
          throw new Error(`Failed to fetch data: ${response.status}`);
        }
      } catch (error) {
      } finally {
      }
    };
    fetchData();
  }, [selectedDate, userInfo]);
  return (
    <>
      <Breadcrumb>
        <BreadcrumbItem>
          <Nav.Link as={Link} to="/dashboard" className="text-success text-decoration-none">
            Dashboard
          </Nav.Link>
        </BreadcrumbItem>
        <BreadcrumbItem active>Admin Dashboard</BreadcrumbItem>
      </Breadcrumb>

      <Container fluid className="p-2">
        {/* Welcome and Pay Run + Month Selection */}
        <Row className="mb-4 justify-content-center">
          <Col xs={12} md={8} lg={6} className="text-center">
            <Card className="shadow-sm border-0 p-4 mb-2" style={{ borderRadius: 16 }}>
              <h5 className="mb-2 fw-bold text-primary">Welcome {userName}!</h5>
              <div className="d-flex flex-column flex-md-row align-items-center justify-content-center mb-2 gap-2">
                <span>
                  Process Pay Run for <strong>{selectedDate.toLocaleString("default", { month: "long", year: "numeric" })}</strong>
                </span>
                <Badge bg="warning" text="dark" className="ms-2">
                  Approved
                </Badge>
              </div>
              <div className="d-flex justify-content-center align-items-center mt-2">
                <select
                  className="form-select w-auto"
                  style={{ minWidth: 180, borderRadius: 8 }}
                  value={selectedDate.toISOString()}
                  onChange={(e) => setSelectedDate(new Date(e.target.value))}
                  aria-label="Select Month"
                >
                  {monthOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <small className="text-muted d-block mt-2">
                📌 Pay your employees on 31/{selectedDate.toLocaleString("default", { month: "2-digit", year: "numeric" })}. Record it here
                once you made the payment.
              </small>
            </Card>
          </Col>
        </Row>

        {/* White-Themed Summary Cards */}
        <Row className="g-4 mb-4 justify-content-center">
          <Col xs={12} md={6} lg={4}>
            <Card
              className="shadow-lg border-0 p-4 h-100"
              style={{ borderRadius: 20, background: "#fff", color: "#222", boxShadow: "0 8px 32px rgba(0,0,0,0.08)" }}
            >
              <div className="d-flex align-items-center mb-2">
                <div
                  className="rounded-circle bg-light d-flex align-items-center justify-content-center me-3"
                  style={{ width: 48, height: 48 }}
                >
                  <BiLeaf size={28} className="text-primary" />
                </div>
                <h6 className="mb-0 fw-bold" style={{ letterSpacing: 1, fontSize: "1.25rem" }}>
                  Employees’ Net Pay
                </h6>
              </div>
              <h2 className="fw-bold mb-1" style={{ fontSize: "2.7rem", textShadow: "0 2px 8px rgba(0,0,0,0.08)" }}>
                {formatCurrency(findItem("Employee's Net Salary").InfoField)}
              </h2>
              <span
                className="badge bg-light text-primary px-3 py-2 mt-2"
                style={{ fontSize: "1.15rem", borderRadius: 12, fontWeight: 600 }}
              >
                {findItem("Payment Date").InfoField}
              </span>
            </Card>
          </Col>
          <Col xs={6} md={3} lg={2}>
            <Card
              className="shadow-lg border-0 p-4 h-100 text-center"
              style={{ borderRadius: 20, background: "#fff", color: "#222", boxShadow: "0 8px 32px rgba(0,0,0,0.08)" }}
            >
              <h6 className="fw-bold mb-2" style={{ letterSpacing: 1, fontSize: "1.15rem" }}>
                Payment Date
              </h6>
              <h4 className="fw-bold mb-0" style={{ fontSize: "2rem" }}>
                {findItem("Payment Date").InfoField}
              </h4>
            </Card>
          </Col>
          <Col xs={6} md={3} lg={2}>
            <Card
              className="shadow-lg border-0 p-4 h-100 text-center"
              style={{ borderRadius: 20, background: "#fff", color: "#222", boxShadow: "0 8px 32px rgba(0,0,0,0.08)" }}
            >
              <h6 className="fw-bold mb-2" style={{ letterSpacing: 1, fontSize: "1.15rem" }}>
                No. of Employees
              </h6>
              <h4 className="fw-bold mb-0" style={{ fontSize: "2rem" }}>
                {findItem("Employees").InfoField}
              </h4>
            </Card>
          </Col>
        </Row>

        {/* White-Themed Deduction Summary & Employee Summary */}
        <Row className="g-4 mb-4 justify-content-center">
          <Col xs={12} md={8} lg={7}>
            <Card
              className="shadow-lg border-0 p-4 h-100"
              style={{ borderRadius: 20, background: "#fff", color: "#222", boxShadow: "0 8px 32px rgba(0,0,0,0.08)" }}
            >
              <div className="d-flex flex-wrap gap-3 justify-content-between align-items-center mb-3">
                <h6 className="fw-bold text-uppercase mb-0" style={{ letterSpacing: 1, color: "#222" }}>
                  Deduction Summary
                </h6>
                <Dropdown>
                  <Dropdown.Menu>
                    <Dropdown.Item>This Month</Dropdown.Item>
                    <Dropdown.Item>Previous Month</Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
              <Row className="g-3">
                <Col xs={12} sm={4}>
                  <Card
                    className="border-0 shadow-sm p-4 h-100 text-center"
                    style={{ borderRadius: 16, background: "#f8f9fa", color: "#222" }}
                  >
                    <div
                      className="d-inline-flex align-items-center justify-content-center mb-2 rounded-circle bg-light"
                      style={{ width: 48, height: 48 }}
                    >
                      <BiLeaf size={28} className="text-primary" />
                    </div>
                    <h6 className="fw-bold" style={{ fontSize: "1.15rem", color: "#222" }}>
                      EPF
                    </h6>
                    <h5 className="fw-bold text-primary" style={{ fontSize: "1.35rem" }}>
                      {formatCurrency(findItem("EPF").InfoField)}
                    </h5>
                  </Card>
                </Col>
                <Col xs={12} sm={4}>
                  <Card
                    className="border-0 shadow-sm p-4 h-100 text-center"
                    style={{ borderRadius: 16, background: "#f8f9fa", color: "#222" }}
                  >
                    <div
                      className="d-inline-flex align-items-center justify-content-center mb-2 rounded-circle bg-light"
                      style={{ width: 48, height: 48 }}
                    >
                      <LuShieldPlus size={28} className="text-warning" />
                    </div>
                    <h6 className="fw-bold" style={{ fontSize: "1.15rem", color: "#222" }}>
                      ESI
                    </h6>
                    <h5 className="fw-bold text-warning" style={{ fontSize: "1.35rem" }}>
                      {formatCurrency(findItem("ESI").InfoField)}
                    </h5>
                  </Card>
                </Col>
                <Col xs={12} sm={4}>
                  <Card
                    className="border-0 shadow-sm p-4 h-100 text-center"
                    style={{ borderRadius: 16, background: "#f8f9fa", color: "#222" }}
                  >
                    <div
                      className="d-inline-flex align-items-center justify-content-center mb-2 rounded-circle bg-light"
                      style={{ width: 48, height: 48 }}
                    >
                      <AiOutlinePercentage size={28} className="text-danger" />
                    </div>
                    <h6 className="fw-bold" style={{ fontSize: "1.15rem", color: "#222" }}>
                      TDS Deduction
                    </h6>
                    <h5 className="fw-bold text-danger" style={{ fontSize: "1.35rem" }}>
                      {formatCurrency(findItem("RECTDS").InfoField)}
                    </h5>
                  </Card>
                </Col>
              </Row>
            </Card>
          </Col>
          <Col xs={12} md={4} lg={3}>
            <Card
              className="shadow-lg border-0 p-4 h-100 text-center d-flex flex-column justify-content-center"
              style={{ borderRadius: 20, background: "#fff", color: "#222", boxShadow: "0 8px 32px rgba(0,0,0,0.08)" }}
            >
              <h6 className="fw-bold mb-2" style={{ letterSpacing: 1, fontSize: "1.25rem", color: "#222" }}>
                Employee Summary
              </h6>
              <h2 className="fw-bold mb-2" style={{ fontSize: "3rem", textShadow: "0 2px 8px rgba(0,0,0,0.08)", color: "#222" }}>
                {findItem("Employees").InfoField}
              </h2>
            </Card>
          </Col>
        </Row>

        {/* Removed Net Pay Trend Chart Section as requested */}
      </Container>
    </>
  );
};

export default AdminDashboard;
